'use client'

import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import { EvilBarChart } from '@/components/evilcharts/recharts-bar-chart'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  fillSpendGaps,
  STATS_PRESETS,
  type StatsPreset,
  statsRange,
  utcDay,
  weekStartDay,
} from '@/features/settings/billing/credits/lib/credits'
import { useCreditFormat } from '@/features/settings/billing/credits/use-credit-format'
import { useCreditSpendStats } from '@/service/api-billing.hook'
import { useListApiKeys } from '@/service/api-key.hook'

const BREAKDOWNS = ['dataset', 'source', 'key'] as const
type Breakdown = (typeof BREAKDOWNS)[number]

const BREAKDOWN_FIELD = {
  dataset: 'byDataset',
  source: 'bySource',
  key: 'byKey',
} as const

// Monochrome on purpose (usage, not a signal): the foreground token, which already differs per
// theme, so both theme slots point at it.
const FOREGROUND = { light: ['var(--color-foreground)'], dark: ['var(--color-foreground)'] }

// Longer windows get a zoom brush under the chart.
const BRUSH_MIN_DAYS = 31

/**
 * Gross usage per UTC day. What was charged is net of refunds and lives in the history; this
 * chart answers "what did I use", so it deliberately doesn't subtract them.
 */
export default function CreditUsageCard() {
  const t = useTranslations('settings.billing.credits.usage')
  const { credits } = useCreditFormat()
  const [preset, setPreset] = useState<StatsPreset>('week')
  const [breakdown, setBreakdown] = useState<Breakdown>('dataset')
  const { data, isLoading, isError } = useCreditSpendStats(preset)
  const { data: apiKeys } = useListApiKeys()

  const bars = useMemo(() => {
    if (!data) return []
    const now = new Date()
    const range = statsRange(preset, now)
    return fillSpendGaps(
      data.days,
      range.fromDay ?? weekStartDay(now),
      range.toDay ?? utcDay(now)
    ).map((bar) => ({ ...bar, label: `${bar.day} UTC` }))
  }, [data, preset])

  const rows = useMemo(() => {
    if (!data) return []
    const labels = new Map((apiKeys?.keys ?? []).map((key) => [key.keyId, key.label]))
    return Object.entries(data[BREAKDOWN_FIELD[breakdown]])
      .map(([id, credits]) => ({
        id,
        name: breakdown === 'key' ? (labels.get(id) ?? id.slice(0, 8)) : id,
        credits,
      }))
      .sort((a, b) => b.credits - a.credits)
  }, [apiKeys, breakdown, data])

  return (
    <SettingsCard className="gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SettingsCardHeader title={t('title')} description={t('caption')} />
        <SegmentedControl
          layoutId="credit-usage-preset"
          options={STATS_PRESETS.map((value) => ({ value, label: t(`presets.${value}`) }))}
          value={preset}
          onChange={setPreset}
        />
      </div>

      {isError ? (
        <p className="text-foreground/60 py-6 text-sm">{t('error')}</p>
      ) : (
        <>
          {data && (
            <div className="flex items-baseline gap-2">
              <span className="text-foreground/50 text-xs">{t('total')}</span>
              <span className="text-foreground text-lg font-semibold tabular-nums">
                {credits(data.total)}
              </span>
            </div>
          )}

          {/* Hovering a day dims the others; the full UTC date is the category
              (it titles the tooltip) and the axis shortens it to MM-DD. */}
          <EvilBarChart
            className="aspect-auto h-40"
            data={bars}
            xDataKey="label"
            isLoading={isLoading}
            barRadius={0}
            animationType="none"
            config={{ total: { label: t('total'), colors: FOREGROUND } }}
          >
            <EvilBarChart.XAxis dataKey="label" tickFormatter={(v: string) => v.slice(5, 10)} />
            <EvilBarChart.YAxis
              width={48}
              allowDecimals={false}
              tickFormatter={(value: number) => credits(value, { maximumFractionDigits: 0 })}
            />
            <EvilBarChart.Tooltip
              variant="frosted-glass"
              roundness="sm"
              valueFormatter={(v) => credits(v)}
            />
            <EvilBarChart.Bar dataKey="total" enableHoverHighlight />
            {bars.length > BRUSH_MIN_DAYS && <EvilBarChart.Brush />}
          </EvilBarChart>

          {data && (
            <div className="flex flex-col items-start gap-2">
              <SegmentedControl
                layoutId="credit-usage-breakdown"
                options={BREAKDOWNS.map((value) => ({ value, label: t(`by.${value}`) }))}
                value={breakdown}
                onChange={setBreakdown}
                size="xs"
              />
              {rows.length === 0 ? (
                <p className="text-foreground/50 text-sm">{t('empty')}</p>
              ) : (
                <ul className="divide-foreground/10 flex w-full flex-col divide-y">
                  {rows.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-4 py-1.5">
                      <span className="text-foreground/80 truncate font-mono text-xs">
                        {row.name}
                      </span>
                      <span className="text-foreground text-sm tabular-nums">
                        {credits(row.credits)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </SettingsCard>
  )
}
